import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "./config/API";

function BedsListForm() {
  const [beds, setBeds] = useState([]);

  // =========================================================
  // FILTER STATES
  // =========================================================

  const [selectedFloor, setSelectedFloor] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const [selectedBookingStatus, setSelectedBookingStatus] = useState("");

  // =========================================================
  // FLOOR & ROOM FROM NAVIGATION
  // =========================================================

  const floorName = location.state?.floorName ?? null;
  const roomName = location.state?.roomName ?? null;

  // =========================================================
  // USER DETAILS
  // =========================================================

  const emailId = JSON.parse(localStorage.getItem("user") || "{}")?.emailId;

  // =========================================================
  // FETCH BEDS
  // =========================================================

  useEffect(() => {
    const fetchBeds = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        const payload = {
          emailId,
          searchType: "ALL_BEDS",
          bedName: "",
          ...(floorName && { floorName }),
          ...(roomName && { roomName }),
        };

        const response = await fetch(
          `${API.V1}/floor_rooms/getFloorsRoomsBedsDetails`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.jwtToken}`,
            },
            body: JSON.stringify(payload),
          },
        );

        if (response.status === 401) {
          alert("Session Expired. Please login again.");
          localStorage.clear();
          navigate("/");
          return;
        }

        const data = await response.json();

        if (data?.data?.bedsList) {
          setBeds(data.data.bedsList);
        } else {
          setBeds([]);
        }
      } catch (error) {
        console.error("Error fetching beds:", error);
        setBeds([]);
      }
    };

    if (emailId) {
      fetchBeds();
    }
  }, [emailId, floorName, roomName, navigate]);

  // =========================================================
  // UNIQUE FLOORS
  // =========================================================

  const floorOptions = useMemo(() => {
    const floors = [
      ...new Set(beds.map((bed) => bed.floorName).filter(Boolean)),
    ];

    return floors.sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [beds]);

  // =========================================================
  // ROOMS BASED ON FLOOR
  // =========================================================

  const roomOptions = useMemo(() => {
    const filteredBeds = selectedFloor
      ? beds.filter((bed) => bed.floorName === selectedFloor)
      : beds;

    const rooms = [
      ...new Set(filteredBeds.map((bed) => bed.roomName).filter(Boolean)),
    ];

    return rooms.sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [beds, selectedFloor]);

  // =========================================================
  // FILTERED BEDS
  // =========================================================

  const filteredBeds = useMemo(() => {
    return beds.filter((bed) => {
      const floorMatch = !selectedFloor || bed.floorName === selectedFloor;

      const roomMatch = !selectedRoom || bed.roomName === selectedRoom;

      const bookingStatusMatch =
        selectedBookingStatus === ""
          ? true
          : selectedBookingStatus === "BOOKED"
            ? bed.bedBookingStatus === true
            : selectedBookingStatus === "AVAILABLE"
              ? bed.bedBookingStatus === false
              : true;

      return floorMatch && roomMatch && bookingStatusMatch;
    });
  }, [beds, selectedFloor, selectedRoom, selectedBookingStatus]);

  // =========================================================
  // ROOM SHARING MAP
  // =========================================================

  const roomSharingMap = useMemo(() => {
    const map = {};

    beds.forEach((bed) => {
      const key = `${bed.floorName}_${bed.roomName}`;

      map[key] = (map[key] || 0) + 1;
    });

    return map;
  }, [beds]);

  // =========================================================
  // FLOOR CHANGE
  // =========================================================

  const handleFloorChange = (e) => {
    const value = e.target.value;

    setSelectedFloor(value);
    setSelectedRoom("");
  };

  // =========================================================
  // ROOM CHANGE
  // =========================================================

  const handleRoomChange = (e) => {
    setSelectedRoom(e.target.value);
  };

  // =========================================================
  // RESET FILTERS
  // =========================================================

  const handleResetFilters = () => {
    setSelectedFloor("");
    setSelectedRoom("");
    setSelectedBookingStatus("");
  };

  // =========================================================
  // UPDATE BED
  // =========================================================

  const handleUpdate = (bed) => {
    navigate("/createBedForm", {
      state: {
        bed,
      },
    });
  };

  // =========================================================
  // DELETE BED
  // =========================================================

  const handleDelete = async (bedId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this bed?",
    );

    if (!confirmDelete) return;

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const response = await fetch(`${API.V1}/floor_rooms/deleteBed/${bedId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${user?.jwtToken}`,
        },
      });

      if (response.status === 401) {
        alert("Session Expired. Please login again.");
        localStorage.clear();
        navigate("/");
        return;
      }

      const data = await response.json();

      if (data?.statusCode === 200) {
        setBeds((prevBeds) => prevBeds.filter((bed) => bed.bedId !== bedId));

        alert("Bed deleted successfully.");
      } else {
        alert(data?.message || "Failed to delete bed.");
      }
    } catch (error) {
      console.error("Delete bed error:", error);
      alert("Server Error. Please try again.");
    }
  };

  // =========================================================
  // BOOKING STATUS
  // =========================================================

  const getBookingStatus = (bed) => {
    if (bed.bookingStatus !== undefined && bed.bookingStatus !== null) {
      return bed.bookingStatus;
    }

    if (bed.bookedStatus !== undefined && bed.bookedStatus !== null) {
      return bed.bookedStatus;
    }

    if (bed.isBooked !== undefined && bed.isBooked !== null) {
      return bed.isBooked ? "Booked" : "Available";
    }

    if (bed.bedBookingStatus !== undefined && bed.bedBookingStatus !== null) {
      return bed.bedBookingStatus;
    }

    return "Available";
  };

  // =========================================================
  // BOOKING STATUS STYLE
  // =========================================================

  const getBookingStatusStyle = (bed) => {
    return bed.bedBookingStatus ? styles.bookedStatus : styles.availableStatus;
  };

  // =========================================================
  // FORMAT PRICE
  // =========================================================

  const formatPrice = (value) => {
    if (value === undefined || value === null || value === "") {
      return "-";
    }

    return Number(value).toLocaleString("en-IN");
  };

  // =========================================================
  // RETURN
  // =========================================================

  return (
    <div style={styles.container}>
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.title}>
            Beds
            {floorName ? ` - ${floorName}` : ""}
            {roomName ? ` / ${roomName}` : ""}
          </h2>

          <p style={styles.subtitle}>
            Manage beds, pricing, booking status and availability
          </p>
        </div>

        <div style={styles.countBox}>
          <span style={styles.countLabel}>Total Beds</span>

          <span style={styles.countNumber}>{filteredBeds.length}</span>
        </div>
      </div>

      {/* =====================================================
          FILTER SECTION
      ===================================================== */}

      <div style={styles.filterCard}>
        <div style={styles.filterTitle}>Filter Beds</div>

        <div style={styles.filterContainer}>
          {/* FLOOR */}

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Floor</label>

            <select
              value={selectedFloor}
              onChange={handleFloorChange}
              style={styles.filterInput}
            >
              <option value="">All Floors</option>

              {floorOptions.map((floor) => (
                <option key={floor} value={floor}>
                  {floor}
                </option>
              ))}
            </select>
          </div>

          {/* ROOM */}

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Room</label>

            <select
              value={selectedRoom}
              onChange={handleRoomChange}
              style={styles.filterInput}
            >
              <option value="">All Rooms</option>

              {roomOptions.map((room) => (
                <option key={room} value={room}>
                  {room}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Booking Status</label>

            <select
              value={selectedBookingStatus}
              onChange={(e) => setSelectedBookingStatus(e.target.value)}
              style={styles.filterInput}
            >
              <option value="">All Beds</option>
              <option value="AVAILABLE">Available Beds</option>
              <option value="BOOKED">Booked Beds</option>
            </select>
          </div>

          {/* RESET */}

          <button
            type="button"
            onClick={handleResetFilters}
            style={styles.resetBtn}
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* =====================================================
          FILTER INFO
      ===================================================== */}

      {(selectedFloor || selectedRoom || selectedBookingStatus) && (
        <div style={styles.filterInfo}>
          <div style={styles.filterInfoLeft}>
            <span style={styles.showingText}>Showing</span>

            {selectedFloor && (
              <span style={styles.filterBadge}>Floor: {selectedFloor}</span>
            )}

            {selectedRoom && (
              <span style={styles.filterBadge}>Room: {selectedRoom}</span>
            )}
            {selectedBookingStatus && (
              <span style={styles.filterBadge}>
                Booking:{" "}
                {selectedBookingStatus === "BOOKED" ? "Booked" : "Available"}
              </span>
            )}

            <span style={styles.bedCountText}>{filteredBeds.length} beds</span>
          </div>

          <button
            type="button"
            onClick={handleResetFilters}
            style={styles.clearFilterBtn}
          >
            Clear
          </button>
        </div>
      )}

      {/* =====================================================
          TABLE WRAPPER
      ===================================================== */}

      <div style={styles.tableWrapper}>
        {/* ===================================================
            HEADER
        =================================================== */}

        <div style={styles.headerRow}>
          <div>Bed Name</div>
          <div>Floor</div>
          <div>Room</div>
          <div>Booking Status</div>
          <div>Actual Price/M</div>
          <div>Offer Price/M</div>
          <div>Release Date</div>
          <div>Cost/Day</div>
          <div>Status</div>
          <div>Actions</div>
        </div>

        {/* ===================================================
            BEDS
        =================================================== */}

        {filteredBeds.length > 0 ? (
          filteredBeds.map((bed) => {
            const bookingStatus = getBookingStatus(bed);

            return (
              <div key={bed.bedId} style={styles.row}>
                {/* BED NAME */}

                <div style={styles.bedNameCell}>
                  <div style={styles.bedName}>{bed.bedName || "-"}</div>

                  {bed.bedId && <div style={styles.bedId}>ID: {bed.bedId}</div>}
                </div>

                {/* FLOOR */}

                <div style={styles.cell}>{bed.floorName || "-"}</div>

                {/* ROOM */}

                <div style={styles.cell}>
                  <div>{bed.roomName || "-"}</div>

                  {bed.floorName &&
                    bed.roomName &&
                    roomSharingMap[`${bed.floorName}_${bed.roomName}`] && (
                      <small style={styles.sharingText}>
                        {roomSharingMap[`${bed.floorName}_${bed.roomName}`]}{" "}
                        Sharing
                      </small>
                    )}
                </div>

                {/* BOOKING STATUS */}

                <div style={styles.cell}>
                  <span style={getBookingStatusStyle(bed)}>
                    {bed.bedBookingStatus ? "Booked" : "Available"}
                  </span>
                </div>

                {/* ACTUAL PRICE */}

                <div style={styles.priceCell}>
                  ₹ {formatPrice(bed.actualPrice)}
                </div>

                {/* OFFER PRICE */}

                <div style={styles.offerPriceCell}>
                  ₹ {formatPrice(bed.offerPrice)}
                </div>

                {/* RELEASE DATE */}

                <div style={styles.dateCell}>{bed.offerEndDate || "-"}</div>

                {/* COST PER DAY */}

                <div style={styles.priceCell}>
                  ₹ {formatPrice(bed.bedCostPerDay)}
                </div>

                {/* BED STATUS */}

                <div>
                  <span style={bed.bedStatus ? styles.active : styles.inactive}>
                    <span
                      style={
                        bed.bedStatus
                          ? styles.statusDotActive
                          : styles.statusDotInactive
                      }
                    />

                    {bed.bedStatus ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* ACTIONS */}

                {/* ACTIONS */}
                {/* ACTIONS */}
                <div style={styles.actionBox}>
                  {/* UPDATE */}
                  <button
                    type="button"
                    style={{
                      ...styles.updateBtn,
                      ...(bed.bedBookingStatus ? styles.disabledBtn : {}),
                    }}
                    onClick={() => {
                      if (!bed.bedBookingStatus) {
                        handleUpdate(bed);
                      }
                    }}
                    disabled={bed.bedBookingStatus}
                    title={
                      bed.bedBookingStatus
                        ? "Booked bed cannot be updated"
                        : "Update bed"
                    }
                  >
                    Update
                  </button>

                  {/* DELETE */}
                  <button
                    type="button"
                    style={{
                      ...styles.deleteBtn,
                      ...(bed.bedBookingStatus ? styles.disabledBtn : {}),
                    }}
                    onClick={() => {
                      if (!bed.bedBookingStatus) {
                        handleDelete(bed.bedId);
                      }
                    }}
                    disabled={bed.bedBookingStatus}
                    title={
                      bed.bedBookingStatus
                        ? "Booked bed cannot be deleted"
                        : "Delete bed"
                    }
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div style={styles.noBeds}>
            <div style={styles.noBedsIcon}>🛏️</div>

            <div style={styles.noBedsTitle}>No Beds Found</div>

            <div style={styles.noBedsText}>
              No beds match the selected floor or room.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BedsListForm;

// =========================================================
// STYLES
// =========================================================

const styles = {
  // =======================================================
  // MAIN CONTAINER
  // =======================================================

  container: {
    width: "100%",
    minHeight: "100vh",
    boxSizing: "border-box",
    padding: "24px 28px 40px",
    background: "#f5f7fb",
    color: "#1f2937",
    overflowX: "hidden",
  },

  // =======================================================
  // PAGE HEADER
  // =======================================================

  pageHeader: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "20px",
    marginBottom: "22px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "750",
    color: "#111827",
    letterSpacing: "-0.5px",
  },

  subtitle: {
    margin: "6px 0 0",
    fontSize: "14px",
    color: "#64748b",
  },

  countBox: {
    minWidth: "145px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "12px 18px",
    background: "#2563eb",
    color: "#ffffff",
    borderRadius: "10px",
    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.22)",
    boxSizing: "border-box",
  },

  countLabel: {
    fontSize: "14px",
    fontWeight: "600",
  },

  countNumber: {
    fontSize: "21px",
    fontWeight: "800",
  },

  // =======================================================
  // FILTER CARD
  // =======================================================

  filterCard: {
    width: "100%",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "18px 20px",
    marginBottom: "15px",
    boxSizing: "border-box",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
  },

  filterTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#334155",
    marginBottom: "13px",
  },

  filterContainer: {
    display: "flex",
    alignItems: "flex-end",
    gap: "16px",
    flexWrap: "wrap",
  },

  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  filterLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
  },

  filterInput: {
    width: "220px",
    height: "42px",
    padding: "0 12px",
    fontSize: "14px",
    borderRadius: "7px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#1e293b",
    outline: "none",
    cursor: "pointer",
    boxSizing: "border-box",
  },

  resetBtn: {
    height: "42px",
    padding: "0 18px",
    fontSize: "14px",
    fontWeight: "650",
    background: "#475569",
    color: "#ffffff",
    border: "none",
    borderRadius: "7px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  // =======================================================
  // FILTER INFO
  // =======================================================

  filterInfo: {
    width: "100%",
    minHeight: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "15px",
    padding: "9px 14px",
    marginBottom: "15px",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "9px",
    boxSizing: "border-box",
  },

  filterInfoLeft: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    flexWrap: "wrap",
  },

  showingText: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
  },

  filterBadge: {
    padding: "5px 10px",
    borderRadius: "6px",
    background: "#dbeafe",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: "700",
  },

  bedCountText: {
    fontSize: "13px",
    color: "#64748b",
    fontWeight: "600",
  },

  clearFilterBtn: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
  },

  // =======================================================
  // TABLE WRAPPER
  // =======================================================

  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    paddingBottom: "8px",
  },

  // =======================================================
  // TABLE HEADER
  // =======================================================

  headerRow: {
    minWidth: "1420px",
    display: "grid",

    gridTemplateColumns:
      "1.35fr 0.95fr 0.95fr 1.2fr 1.15fr 1.15fr 1.15fr 1fr 0.95fr 1.55fr",

    gap: "0",

    alignItems: "center",

    background: "linear-gradient(135deg, #1e3a8a, #1d4ed8)",

    color: "#ffffff",

    padding: "15px 16px",

    borderRadius: "10px",

    fontWeight: "700",

    fontSize: "13px",

    marginBottom: "9px",

    boxSizing: "border-box",

    boxShadow: "0 3px 8px rgba(30, 58, 138, 0.18)",
  },

  // =======================================================
  // TABLE ROW
  // =======================================================

  row: {
    minWidth: "1420px",

    display: "grid",

    gridTemplateColumns:
      "1.35fr 0.95fr 0.95fr 1.2fr 1.15fr 1.15fr 1.15fr 1fr 0.95fr 1.55fr",

    gap: "0",

    alignItems: "center",

    background: "#ffffff",

    padding: "14px 16px",

    borderRadius: "10px",

    marginBottom: "8px",

    minHeight: "72px",

    boxSizing: "border-box",

    fontSize: "13px",

    color: "#334155",

    fontWeight: "500",

    border: "1px solid #e5e7eb",

    boxShadow: "0 2px 7px rgba(15, 23, 42, 0.04)",
  },

  // =======================================================
  // CELLS
  // =======================================================

  cell: {
    padding: "0 8px",
    lineHeight: "1.45",
    wordBreak: "break-word",
  },

  bedNameCell: {
    padding: "0 8px",
    minWidth: 0,
  },

  bedName: {
    color: "#2563eb",
    fontWeight: "750",
    fontSize: "14px",
    wordBreak: "break-word",
  },

  bedId: {
    marginTop: "3px",
    fontSize: "11px",
    color: "#94a3b8",
    fontWeight: "500",
  },

  sharingText: {
    display: "block",
    marginTop: "4px",
    color: "#94a3b8",
    fontSize: "10px",
    fontWeight: "500",
  },

  priceCell: {
    padding: "0 8px",
    fontWeight: "650",
    color: "#334155",
    whiteSpace: "nowrap",
  },

  offerPriceCell: {
    padding: "0 8px",
    fontWeight: "700",
    color: "#16a34a",
    whiteSpace: "nowrap",
  },

  disabledBtn: {
    background: "#cbd5e1",
    color: "#64748b",
    cursor: "not-allowed",
    opacity: 0.7,
    boxShadow: "none",
  },

  dateCell: {
    padding: "0 8px",
    fontSize: "12px",
    color: "#475569",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },

  // =======================================================
  // BOOKING STATUS
  // =======================================================

  availableStatus: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 9px",
    borderRadius: "20px",
    background: "#dcfce7",
    color: "#15803d",
    fontSize: "11px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  bookedStatus: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 9px",
    borderRadius: "20px",
    background: "#fee2e2",
    color: "#b91c1c",
    fontSize: "11px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  // =======================================================
  // BED STATUS
  // =======================================================

  active: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#16a34a",
    fontWeight: "700",
    fontSize: "12px",
    whiteSpace: "nowrap",
  },

  inactive: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#dc2626",
    fontWeight: "700",
    fontSize: "12px",
    whiteSpace: "nowrap",
  },

  statusDotActive: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: "#16a34a",
    display: "inline-block",
  },

  statusDotInactive: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: "#dc2626",
    display: "inline-block",
  },

  // =======================================================
  // ACTIONS
  // =======================================================

  actionBox: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "0 8px",
  },

  updateBtn: {
    height: "34px",
    padding: "0 11px",
    background: "#16a34a",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "650",
    whiteSpace: "nowrap",
  },

  deleteBtn: {
    height: "34px",
    padding: "0 11px",
    background: "#dc2626",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "650",
    whiteSpace: "nowrap",
  },

  // =======================================================
  // NO BEDS
  // =======================================================

  noBeds: {
    minWidth: "1420px",
    padding: "55px 20px",
    textAlign: "center",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    boxSizing: "border-box",
  },

  noBedsIcon: {
    fontSize: "35px",
    marginBottom: "8px",
  },

  noBedsTitle: {
    fontSize: "17px",
    fontWeight: "750",
    color: "#334155",
    marginBottom: "5px",
  },

  noBedsText: {
    fontSize: "13px",
    color: "#94a3b8",
  },
};
